import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Logo from "../components/Logo";
import SiteFooter from "../components/SiteFooter";
import { Crown, Sparkles, Users, BrainCircuit, Trophy } from "lucide-react";

export default function Landing() {
  return (
    <div className="min-h-screen bg-tribal">
      <Navbar />

      {/* HERO */}
      <section className="max-w-6xl mx-auto px-6 pt-16 pb-12 grid md:grid-cols-2 gap-12 items-center fade-up">
        <div>
          <p className="text-xs tracking-[0.3em] text-[color:var(--primary)] mb-4" data-testid="hero-overline">
            JEU DE DAMES POLYNÉSIEN
          </p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-glow leading-[1.05]">
            Perē Fa'anu'u.
            <br />
            <span className="text-[color:var(--primary)]">L'art de la stratégie</span> aux îles.
          </h1>
          <p className="mt-6 text-[color:var(--text-secondary)] text-base sm:text-lg max-w-lg">
            Coquillages contre galets. Affrontez vos amis, défiez l'IA, ou
            grimpez le classement mondial. Le jeu se déroule sur les
            <em> intersections </em> du quadrillage — comme à Tahiti.
          </p>

          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/lobby"
              className="px-6 py-3 rounded-md btn-gold text-sm tracking-wide"
              data-testid="hero-cta-play"
            >
              Jouer maintenant
            </Link>
            <Link
              to="/rules"
              className="px-6 py-3 rounded-md border border-[color:var(--border)] hover:bg-[color:var(--surface)] transition text-sm"
              data-testid="hero-cta-rules"
            >
              Découvrir les règles
            </Link>
          </div>

          <div className="mt-10 grid grid-cols-3 gap-4 max-w-md">
            {[
              { icon: <Users size={18} />, label: "1v1 Local" },
              { icon: <BrainCircuit size={18} />, label: "IA Ari'i" },
              { icon: <Trophy size={18} />, label: "Classement" },
            ].map((it) => (
              <div key={it.label} className="glass rounded-md px-3 py-3 text-xs flex items-center gap-2" data-testid={`hero-feat-${it.label}`}>
                <span className="text-[color:var(--primary)]">{it.icon}</span>
                <span className="text-[color:var(--text-secondary)]">{it.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Logo & aperçu plateau */}
        <div className="relative flex flex-col items-center max-w-[480px] mx-auto w-full">
          <div className="mb-6 w-full flex justify-center">
            <Logo size="xl" />
          </div>
          <div className="relative aspect-square w-full">
          <div className="absolute inset-0 bg-wood rounded-xl border-2 border-[#3a2410] shadow-2xl" />
          <svg viewBox="0 0 700 700" className="absolute inset-6">
            {(() => {
              const N = 5;
              const pos = (i) => ((i + 0.5) * 700) / N;
              return (
                <>
                  {Array.from({ length: N }).map((_, i) => {
                    const p = pos(i);
                    return (
                      <g key={i}>
                        <line x1={p} y1={pos(0)} x2={p} y2={pos(N - 1)} stroke="#E8E2D2" strokeOpacity="0.9" strokeWidth="1.8" />
                        <line x1={pos(0)} y1={p} x2={pos(N - 1)} y2={p} stroke="#E8E2D2" strokeOpacity="0.9" strokeWidth="1.8" />
                      </g>
                    );
                  })}
                  {Array.from({ length: N - 1 }).flatMap((_, ix) =>
                    Array.from({ length: N - 1 }).map((_, iy) => {
                      const x1 = pos(ix), y1 = pos(iy);
                      const x2 = pos(ix + 1), y2 = pos(iy + 1);
                      return (
                        <g key={`d-${ix}-${iy}`}>
                          <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="#E8E2D2" strokeOpacity="0.7" strokeWidth="1.2" />
                          <line x1={x2} y1={y1} x2={x1} y2={y2} stroke="#E8E2D2" strokeOpacity="0.7" strokeWidth="1.2" />
                        </g>
                      );
                    })
                  )}
                </>
              );
            })()}
            {/* a few decorative pieces on intersections */}
            <circle cx={70} cy={70} r={40} fill="#f9f9f9" />
            <circle cx={350} cy={490} r={40} fill="#0a0a0a" />
            <circle cx={630} cy={210} r={40} fill="#f9f9f9" />
          </svg>
          <Crown
            size={48}
            className="absolute -top-4 -right-4 text-[color:var(--primary)] drop-shadow-lg"
          />
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-6xl mx-auto px-6 py-16 grid md:grid-cols-3 gap-6">
        {[
          {
            t: "Authentique",
            d: "Règles polynésiennes traditionnelles : jeu sur intersections, prise obligatoire, Tapea, immunité du Ari'i.",
            icon: <Sparkles className="text-[color:var(--primary)]" />,
          },
          {
            t: "IA Ari'i",
            d: "Affrontez une IA minimax intelligente qui anticipe vos coups et privilégie les captures multiples.",
            icon: <BrainCircuit className="text-[color:var(--primary)]" />,
          },
          {
            t: "En ligne & classement",
            d: "Matchmaking rapide, chat de partie, ELO et leaderboard mondial pour les meilleurs joueurs.",
            icon: <Trophy className="text-[color:var(--primary)]" />,
          },
        ].map((f) => (
          <div key={f.t} className="glass rounded-lg p-6 fade-up" data-testid={`feat-${f.t}`}>
            <div className="mb-3">{f.icon}</div>
            <h3 className="text-xl font-semibold mb-2">{f.t}</h3>
            <p className="text-sm text-[color:var(--text-secondary)]">{f.d}</p>
          </div>
        ))}
      </section>

      <SiteFooter />
    </div>
  );
}
